import React, { useEffect, useRef, useState } from "react";
import { Loader2, AlertTriangle, Printer, Download } from "lucide-react";

// PDF con caricamento pigro: la prima pagina si vede subito, le altre si disegnano solo quando entrano
// nello schermo (IntersectionObserver). Rendering locale con pdf.js: nessun download automatico, funziona
// anche dove il browser non ha un visualizzatore PDF (Android, PWA).
import { apriPdf } from "../pdfjs";

function Pagina({ doc, num, larghezza, subito }) {
  const ref = useRef(null);
  const [visibile, setVisibile] = useState(subito);
  const [altezza, setAltezza] = useState(Math.round(larghezza * 1.414));
  useEffect(() => {
    if (visibile || !ref.current) return;
    const io = new IntersectionObserver((en) => en.some((e) => e.isIntersecting) && setVisibile(true), { rootMargin: "600px 0px" });
    io.observe(ref.current);
    return () => io.disconnect();
  }, [visibile]);
  useEffect(() => {
    if (!visibile) return;
    let annullato = false;
    (async () => {
      const page = await doc.getPage(num);
      const base = page.getViewport({ scale: 1 });
      const scala = larghezza / base.width;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const vp = page.getViewport({ scale: scala * dpr });
      const canvas = ref.current?.querySelector("canvas");
      if (!canvas || annullato) return;
      canvas.width = vp.width;
      canvas.height = vp.height;
      canvas.style.width = `${larghezza}px`;
      canvas.style.height = `${Math.round(vp.height / dpr)}px`;
      setAltezza(Math.round(vp.height / dpr));
      await page.render({ canvasContext: canvas.getContext("2d"), viewport: vp }).promise;
    })().catch(() => {});
    return () => { annullato = true; };
  }, [visibile, doc, num, larghezza]);
  return (
    <div ref={ref} className="pdf-pagina" style={{ width: larghezza, minHeight: altezza }} data-testid={`pdf-pagina-${num}`}>
      <canvas />
      {!visibile && <span className="pdf-pagina-num">{num}</span>}
    </div>
  );
}

export default function PdfPreview({ blob, nome, onStampa, onScarica }) {
  const [doc, setDoc] = useState(null);
  const [errore, setErrore] = useState("");
  const [larghezza, setLarghezza] = useState(() => Math.min(900, window.innerWidth - 24));
  useEffect(() => {
    const onResize = () => setLarghezza(Math.min(900, window.innerWidth - 24));
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  useEffect(() => {
    if (!blob) return;
    let vivo = true;
    let d = null;
    (async () => {
      try {
        const buf = await blob.arrayBuffer();
        const testa = new TextDecoder("latin1").decode(new Uint8Array(buf.slice(0, 1024)));
        if (!testa.includes("%PDF")) throw new Error(`il file non è un PDF valido (${blob.size} byte, inizia con "${testa.slice(0, 8).replace(/[^\x20-\x7e]/g, "?")}")`);
        d = await apriPdf(buf);
        if (vivo) setDoc(d);
        else d.destroy();
      } catch (e) {
        if (vivo) setErrore(e?.message || "PDF non leggibile");
      }
    })();
    return () => {
      vivo = false;
      try { d?.destroy(); } catch (e) { console.debug("PdfPreview: destroy del documento", e); }
    };
  }, [blob]);
  if (errore) {
    return (
      <div className="preview-fs-card" data-testid="preview-pdf-error">
        <AlertTriangle size={40} strokeWidth={2} color="#f2c230" />
        <div className="preview-fs-card-name">{nome}</div>
        <p className="preview-fs-card-text" data-testid="preview-state">Anteprima non riuscita: {errore}</p>
        <button className="wf-btn primary preview-fs-download" onClick={onScarica} data-testid="preview-download-btn">
          <Download size={18} strokeWidth={2.6} /> Scarica il PDF
        </button>
      </div>
    );
  }
  if (!doc) return <div className="preview-fs-state" data-testid="preview-state"><Loader2 size={26} className="spin" /> Apro {nome}...</div>;
  return (
    <div className="pdf-scroll" data-testid="preview-pdf">
      <div className="pdf-toolbar" data-testid="pdf-toolbar">
        <span className="pdf-info">{doc.numPages} {doc.numPages === 1 ? "pagina" : "pagine"}</span>
        <button className="wf-btn pdf-tool" onClick={onStampa} data-testid="pdf-print"><Printer size={16} strokeWidth={2.4} /> Stampa</button>
        <button className="wf-btn pdf-tool" onClick={onScarica} data-testid="pdf-download"><Download size={16} strokeWidth={2.4} /> Scarica</button>
      </div>
      {Array.from({ length: doc.numPages }, (_, i) => (
        <Pagina key={i + 1} doc={doc} num={i + 1} larghezza={larghezza} subito={i < 2} />
      ))}
    </div>
  );
}
